import AuthenticatorAppService from '../../../application/platform/security/authenticator-app-service';
import ApplicationAppService from '../../../application/platform/application-app-service';
import { ACTION_TYPES, useLoginDispatch, useLoginState } from "./login-context";
import { useBaseLayout } from '../../../components/context/use-base-layout';
import { IPreAuthenticationRequest } from '../../../model/security/request/pre-authentication-request';
import { IAuthenticationRequest } from '../../../model/security/request/authentication-request';
import { IPreAuthenticationResponse } from '../../../model/security/response/pre-authentication-response';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

export const useLoginController = () => {
    const dispatch = useLoginDispatch();
    const state = useLoginState();
    const location = useLocation();
    const navigate = useNavigate();
    const applicationData = ApplicationAppService.getApplicationData();
    const { showMessage } = useBaseLayout();

    useEffect(() => {
        checkInitialState();
    }, []);

    async function checkInitialState() {
        const queryParams = new URLSearchParams(location.search);
        const action = queryParams.get('action');

        if (action === 'logout') {
            showMessage({ open: true, message: 'Logout efetuado com sucesso!' });
        } else if (action === 'login') {
            showMessage({ open: true, message: 'Para acessar o sistema deve efetuar o login.', type: "error" });
        }

        try {
            if (ApplicationAppService.getAuthenticationData()) {
                await AuthenticatorAppService.isAuthenticated();
                navigate(`/${applicationData.initialPage}`);
            }
        } catch (error) {
            ApplicationAppService.clearAuthenticationData();
            showMessage({
                open: true,
                message: 'Não foi possível verificar o estado da sessão',
                detail: (error as any).details || (error as Error).message,
                type: 'error'
            });
        }
    }

    function handleOnChange(e: any) {
        let type = state.preAuthenticationRequest.type;
        let user = state.preAuthenticationRequest.user;
        let multiTenantId = state.preAuthenticationRequest.multiTenantId;
        let password = state.preAuthenticationRequest.password;
        let companyId = state.authenticationRequest.companyId;
        let remember = state.authenticationRequest.remember;

        if (e.target.name === "select-type") {
            type = e.target.value;

            if (e.target.value === 2 && applicationData !== undefined && applicationData.multitenant !== undefined) {
                companyId = applicationData.multitenant;
            } else if (e.target.value === 1 && applicationData !== undefined && applicationData.multitenant !== undefined) {
                multiTenantId = applicationData.multitenant;
            }
        }
        if (e.target.name === "input-user") {
            user = e.target.value;
        }
        if (e.target.name === "input-password") {
            password = e.target.value;
        }
        if (e.target.name === "select-company") {
            companyId = e.target.value;
        }
        if (e.target.name === "check-remember") {
            remember = e.target.checked;
        }

        dispatch({
            type: ACTION_TYPES.setPreAuthenticationRequest,
            payload: { user, password, type, multiTenantId }
        });

        dispatch({
            type: ACTION_TYPES.setAuthenticationRequest,
            payload: { user, password, type, companyId, remember, module: applicationData.moduleId }
        });
    }

    function toggleShowPassword() {
        dispatch({
            type: ACTION_TYPES.setShowPassword,
            payload: !state.showPassword
        });
    }

    function handleCompanyChange(value: number | string | null) {
        const companyId = value as number;

        dispatch({
            type: ACTION_TYPES.setAuthenticationRequest,
            payload: {
                ...state.authenticationRequest,
                companyId
            }
        });
    }

    function validFields(step: number): boolean {
        let isValid = true;

        if (step === 1) {
            if (state.preAuthenticationRequest.type === undefined || state.preAuthenticationRequest.type === 0) {
                isValid = false;
            }

            if (state.preAuthenticationRequest.user === undefined || state.preAuthenticationRequest.user.length === 0) {
                isValid = false;
            }
            if (state.preAuthenticationRequest.password === undefined || state.preAuthenticationRequest.password.length === 0) {
                isValid = false;
            }
        } else if (step === 2) {
            if (state.authenticationRequest.type === undefined || state.authenticationRequest.type === 0) {
                isValid = false;
            }

            if (state.authenticationRequest.user === undefined || state.authenticationRequest.user.length === 0) {
                isValid = false;
            }
            if (state.authenticationRequest.password === undefined || state.authenticationRequest.password.length === 0) {
                isValid = false;
            }

            if (state.authenticationRequest.companyId === undefined || state.authenticationRequest.companyId === 0) {
                isValid = false;
            }
        }

        dispatch({
            type: ACTION_TYPES.setFieldInvalid,
            payload: isValid
        });

        return isValid;
    }

    async function authentication() {
        if ((state.preAuthenticationResponse.companies === undefined || state.preAuthenticationResponse.companies?.length === 0) && (applicationData === undefined || state.preAuthenticationRequest.type === 1)) {
            if (validFields(1)) {
                dispatch({ type: ACTION_TYPES.setLoading, payload: true });
                try {
                    const data = await AuthenticatorAppService.preAuthentication(state.preAuthenticationRequest);
                    dispatch({
                        type: ACTION_TYPES.setPreAuthenticationResponse,
                        payload: data
                    });
                    dispatch({ type: ACTION_TYPES.setLoading, payload: false });
                } catch (error) {
                    dispatch({ type: ACTION_TYPES.setLoading, payload: false });
                    dispatch({
                        type: ACTION_TYPES.setPreAuthenticationResponse,
                        payload: {} as IPreAuthenticationResponse
                    });
                    showMessage({
                        open: true,
                        message: 'Não foi possível validar as credenciais',
                        detail: (error as any).details || (error as Error).message,
                        type: 'error'
                    });
                }
            } else {
                showMessage({ type: 'error', message: 'É necessário preenhcer todos os campos obrigtórios.', open: true });
            }
        } else {
            if (validFields(2)) {
                dispatch({ type: ACTION_TYPES.setLoading, payload: true });
                try {
                    const data = await AuthenticatorAppService.authentication(state.authenticationRequest);
                    ApplicationAppService.setAuthenticationData(data);

                    // Garante que foi salvo antes de navegar
                    const saved = ApplicationAppService.getAuthenticationData();
                    if (saved) {
                        dispatch({ type: ACTION_TYPES.setLoading, payload: false });
                        navigate(`/${applicationData.initialPage}`);
                    } else {
                        dispatch({ type: ACTION_TYPES.setLoading, payload: false });
                        showMessage({ type: 'error', message: 'Erro ao salvar dados de autenticação.', open: true });
                    }
                } catch (error) {
                    dispatch({ type: ACTION_TYPES.setLoading, payload: false });
                    dispatch({
                        type: ACTION_TYPES.setPreAuthenticationResponse,
                        payload: {} as IPreAuthenticationResponse
                    });
                    showMessage({
                        open: true,
                        message: 'Não foi possível completar a autenticação',
                        detail: (error as any).details || (error as Error).message,
                        type: 'error'
                    });
                }
            } else {
                showMessage({ type: 'error', message: 'É necessário preenhcer todos os campos obrigtórios.', open: true });
            }
        }
    }

    function handleBackButton() {
        dispatch({
            type: ACTION_TYPES.resetForm,
            payload: {
                preAuthenticationRequest: {
                    type: 1,
                    user: "",
                    password: "",
                    multiTenantId: applicationData.multitenant
                } as IPreAuthenticationRequest,
                preAuthenticationResponse: {} as IPreAuthenticationResponse,
                authenticationRequest: {
                    type: 1,
                    user: "",
                    password: "",
                    remember: false,
                    module: applicationData.moduleId
                } as IAuthenticationRequest,
            }
        });
    }

    return {
        handleOnChange,
        toggleShowPassword,
        handleCompanyChange,
        authentication,
        handleBackButton,
        applicationData
    };
}
